import assertNever from 'assert-never';

import {
	FirmwareUpdateJobRequest,
	FirmwareUpdateStage,
	FirmwareUpdateStatus,
	ScheduledJobType,
	FirmwareUpdateExecutingStage
} from '@shure/cloud/shared/models/devices';
import { DeviceModel, DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';

import {
	FirmwareUpdateExecutionStage,
	FirmwareUpdateRequestStatus,
	ScheduledJobRequestFragment
} from '../graphql/generated/cloud-sys-api';

import { mapDeviceModelFromSysApi } from './utils';

export function mapScheduledJobRequestFromSysApi(
	scheduledJobRequest: ScheduledJobRequestFragment | null | undefined
): FirmwareUpdateJobRequest | undefined {
	if (scheduledJobRequest === undefined || scheduledJobRequest === null) return undefined;

	if (scheduledJobRequest.details.__typename === 'FirmwareUpdateJobRequestDetails') {
		const device = scheduledJobRequest.details.task.device;
		const isKnown = device.__typename === 'Device';
		const result = scheduledJobRequest.details.result;

		const jobRequest: FirmwareUpdateJobRequest = {
			id: scheduledJobRequest.id,
			type: ScheduledJobType.FirmwareUpdate,
			status: scheduledJobRequest.status,
			jobId: scheduledJobRequest.job.jobId,
			executeAt: scheduledJobRequest.job.executeAt,
			// Only known devices (Device type) have node IDs; unknown devices don't
			task: {
				packageKey: scheduledJobRequest.details.task.packageKey,
				packageVersion: scheduledJobRequest.details.task.packageVersion,
				hosted: scheduledJobRequest.details.task.hosted,
				device: {
					known: isKnown,
					nodeId: isKnown ? device.id : undefined,
					hardwareId: device.hardwareId,
					model: isKnown
						? device.interface?.model
							? mapDeviceModelFromSysApi(device.interface?.model)
							: DeviceModel.UNKNOWN
						: mapDeviceModelFromSysApi(device.model),
					status: isKnown ? <DeviceStatus>device.status : DeviceStatus.Offline,
					name: isKnown ? device.features.name?.name : device.name ?? undefined,
					currentFirmwareVersion: isKnown
						? device.features.firmware?.version
						: device.firmwareVersion ?? undefined
				}
			},
			result: result
				? {
						percentComplete: result.percentComplete,
						// TODO - cleanup when we migrate to FW Update V2.
						stage: mapSysApiFirmwareUpdateRequestStatusToFirmwareUpdateStage(result.status),
						executionStage: mapSysApiFirmwareUpdateExecutionStageToFirmwareUpdateStage(result.stage),
						status: errorStringToFirmwareUpdateStatus(result.error)
				  }
				: undefined
		};

		return jobRequest;
	}

	return assertNever(scheduledJobRequest.details.__typename);
}

function mapSysApiFirmwareUpdateRequestStatusToFirmwareUpdateStage(
	value: FirmwareUpdateRequestStatus
): FirmwareUpdateStage {
	switch (value) {
		case FirmwareUpdateRequestStatus.Pending:
			return FirmwareUpdateStage.Pending;
		case FirmwareUpdateRequestStatus.Complete:
			return FirmwareUpdateStage.Complete;
		case FirmwareUpdateRequestStatus.Errored:
			return FirmwareUpdateStage.Error;
		case FirmwareUpdateRequestStatus.Executing:
			return FirmwareUpdateStage.Executing;
		case FirmwareUpdateRequestStatus.Canceled:
			return FirmwareUpdateStage.Canceled;
		default:
			return assertNever(value);
	}
}

function mapSysApiFirmwareUpdateExecutionStageToFirmwareUpdateStage(
	executionStage: FirmwareUpdateExecutionStage | null
): FirmwareUpdateExecutingStage | undefined {
	if (executionStage === null) {
		return undefined;
	}
	switch (executionStage) {
		case FirmwareUpdateExecutionStage.ReservingDevice:
			return FirmwareUpdateStage.DeviceReserved;
		case FirmwareUpdateExecutionStage.PreInstallValidating:
			return FirmwareUpdateStage.PreInstallValidating;
		case FirmwareUpdateExecutionStage.TransferringFiles:
			return FirmwareUpdateStage.TransferringFiles;
		case FirmwareUpdateExecutionStage.Installing:
			return FirmwareUpdateStage.Installing;
		case FirmwareUpdateExecutionStage.Rebooting:
			return FirmwareUpdateStage.Rebooting;
		case FirmwareUpdateExecutionStage.Verifying:
			return FirmwareUpdateStage.Verifying;
		default:
			return assertNever(executionStage);
	}
}

function errorStringToFirmwareUpdateStatus(error: string | null): FirmwareUpdateStatus {
	if (!error) return FirmwareUpdateStatus.Success;
	switch (error) {
		case 'DEVICE_BUSY':
			return FirmwareUpdateStatus.DeviceBusy;
		case 'CONNECTIVITY_LOSS':
			return FirmwareUpdateStatus.ConnectivityLoss;
		case 'CORRUPT_PACKAGE':
			return FirmwareUpdateStatus.CorruptPackage;
		case 'INVALID_PACKAGE':
			return FirmwareUpdateStatus.InvalidPackage;
		case 'MISSING_PROXY':
			return FirmwareUpdateStatus.MissingProxy;
		case 'NOT_CONNECTED_VIA_USB':
			return FirmwareUpdateStatus.NotConnectionViaUsb;
		case 'NOT_DOCKED':
			return FirmwareUpdateStatus.NotDocked;
		case 'DANTE_LOCKED':
			return FirmwareUpdateStatus.DanteLocked;
		case 'DANTE_NETWORK_MISMATCH':
			return FirmwareUpdateStatus.DanteNetworkMismatch;
		case 'NETWORK_LOCKED':
			return FirmwareUpdateStatus.NetworkLocked;
		case 'QUOTA_EXCEEDED':
			return FirmwareUpdateStatus.QuotaExceeded;
		case 'VERIFICATION_ERROR':
			return FirmwareUpdateStatus.VerificationError;
		default:
			return FirmwareUpdateStatus.UnknownError;
	}
}
